import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    currentSelection,
    inFrame,
    isEditable,
    LIMITS,
    pageLangOf,
    reportHiddenOn,
    reportInCabinet,
    reportLiftedOn,
    reportPageUrl,
} from "../lib/pageContext";
import { onOpenErrorReport } from "../model/openErrorReport";
import type { ErrorReportLang, ErrorReportRequest } from "../model/types";
import cls from "./ErrorReportRoot.module.scss";

// лист — отдельным чанком, только когда его открыли
const ErrorReportSheet = lazy(() => import("./ErrorReportSheet"));

type Opened = { request: ErrorReportRequest; pageUrl: string; route: string; lang: ErrorReportLang; n: number };

const FlagIcon = () => (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
        <path d="M14.4 6 14 4H5v17h2v-7h5.6l.4 2h7V6z" fill="currentColor" />
    </svg>
);

const fullscreenNow = (): boolean => {
    const d = document as Document & { webkitFullscreenElement?: Element | null };
    return !!(d.fullscreenElement || d.webkitFullscreenElement);
};

/**
 * «Сообщить об ошибке» на любой странице (владелец, 23.10.2026): маленькая кнопка «Нашли ошибку?» внизу
 * слева (справа — синяя кнопка поддержки; на телефоне — круглая иконка), Ctrl+Enter с выделенным текстом и
 * один лист для всех, кто зовёт openErrorReport (остров рецептов, «Что в блюде»).
 *
 * Выделение берётся в момент нажатия кнопки (pointerdown): на телефоне к click оно уже снято. Кнопки нет на
 * служебных и полноэкранных страницах, во фрейме и в полноэкранном режиме; над полем ввода внизу страницы
 * и над нижней панелью оболочки (--shell-bottom-h) она поднимается; в кабинете на компьютере — правее
 * боковой панели. Окна страниц (острова рецептов, MUI) её закрывают: она под ними.
 */
export const ErrorReportRoot = ({ routeOf }: { routeOf?: (pathname: string) => string }) => {
    const { pathname } = useLocation();
    const { t } = useTranslation();
    const [opened, setOpened] = useState<Opened | null>(null);
    const [fullscreen, setFullscreen] = useState(false);
    const [framed] = useState(() => inFrame());
    const captured = useRef<string | null>(null);
    const fabRef = useRef<HTMLButtonElement>(null);
    const fromFab = useRef(false);
    const routeOfRef = useRef(routeOf);
    routeOfRef.current = routeOf;
    const hidden = framed || reportHiddenOn(pathname);

    const open = useCallback((request: ErrorReportRequest, viaFab = false) => {
        const path = window.location.pathname;
        let route = "";
        try {
            route = (routeOfRef.current ? routeOfRef.current(path) : "").slice(0, LIMITS.route);
        } catch {
            route = "";
        }
        const selectedText = request.selectedText !== undefined ? request.selectedText : currentSelection();
        setOpened((cur) => ({
            request: { ...request, selectedText },
            pageUrl: reportPageUrl(window.location.href),
            route,
            lang: pageLangOf(path),
            n: (cur?.n || 0) + 1,
        }));
        fromFab.current = viaFab;
    }, []);

    useEffect(() => onOpenErrorReport((request) => open(request)), [open]);

    // Ctrl+Enter (⌘+Enter) с выделенным текстом — лист с цитатой. На window: окно блюда острова рецептов
    // ловит его раньше, на document, и само открывает лист рецепта (RC68) — тогда здесь defaultPrevented.
    useEffect(() => {
        if (hidden) return undefined;
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== "Enter" || !(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey || e.repeat || e.defaultPrevented) return;
            const target = (typeof e.composedPath === "function" && e.composedPath()[0]) || e.target;
            if (isEditable(target) || isEditable(document.activeElement)) return;
            const selectedText = currentSelection();
            if (!selectedText) return;
            e.preventDefault();
            open({ kind: "page", selectedText });
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [hidden, open]);

    useEffect(() => {
        const on = () => setFullscreen(fullscreenNow());
        document.addEventListener("fullscreenchange", on);
        document.addEventListener("webkitfullscreenchange", on);
        return () => {
            document.removeEventListener("fullscreenchange", on);
            document.removeEventListener("webkitfullscreenchange", on);
        };
    }, []);

    // Открыли кнопкой — закрыли лист, фокус снова на ней (Safari кнопку по клику не фокусирует).
    const close = useCallback(() => {
        setOpened(null);
        if (fromFab.current) window.setTimeout(() => fabRef.current?.focus({ preventScroll: true }), 0);
    }, []);

    // Под открытым листом кнопка остаётся (её закрывает подложка листа): закрыли лист — фокус вернётся на неё.
    const fab =
        !hidden && !fullscreen
            ? createPortal(
                  <button
                      ref={fabRef}
                      type="button"
                      className={cls.fab}
                      data-error-report-fab=""
                      data-lifted={reportLiftedOn(pathname) || undefined}
                      data-cabinet={reportInCabinet(pathname) || undefined}
                      aria-label={String(t("errorReport.fabAria"))}
                      title={String(t("errorReport.fabAria"))}
                      onPointerDown={() => {
                          captured.current = currentSelection();
                      }}
                      onClick={() => {
                          const selectedText = captured.current || currentSelection();
                          captured.current = null;
                          open({ kind: "page", selectedText }, true);
                      }}
                  >
                      <FlagIcon />
                      <span className={cls.text}>{String(t("errorReport.fab"))}</span>
                  </button>,
                  document.body,
              )
            : null;

    return (
        <>
            {fab}
            {opened && (
                <Suspense fallback={null}>
                    <ErrorReportSheet
                        key={opened.n}
                        request={opened.request}
                        lang={opened.lang}
                        route={opened.route}
                        pageUrl={opened.pageUrl}
                        onClose={close}
                    />
                </Suspense>
            )}
        </>
    );
};

export default ErrorReportRoot;
